import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, ArrowRight, Bot, Copy, ExternalLink, Globe, HelpCircle, Loader2, Mail, MessageCircle, MoreHorizontal,
  PenLine, Plug, Smartphone, Trash2, UserCheck, ShieldCheck,
} from 'lucide-react';
import type { OrderResponse } from '../../../services/OrderService';
import OrderAttentionStrip from './OrderAttentionStrip';
import { MicroStepper } from './OrderLifecycleCell';
import {
  STAGE_LABEL, STAGE_STYLE, channelOf, formatDwell, hoursSince, isPastSla, lifecycleRowClass, nextStepOf,
  ownerTypeOf, partsCounts, stageOf, subjectOf, vehicleOf,
} from './orderLifecycle';

/** Grid template shared by the header and every row (mobile = 2×2 card, md+ = one 48px line). */
export const ROW_GRID =
  'grid items-center gap-x-3 px-3 ' +
  'grid-cols-[minmax(0,1fr)_auto_28px] ' +
  'md:grid-cols-[minmax(150px,1fr)_minmax(170px,1fr)_minmax(120px,0.9fr)_44px_124px_28px] ' +
  'lg:grid-cols-[minmax(160px,1fr)_minmax(200px,1fr)_92px_128px_112px_136px_28px] ' +
  'xl:grid-cols-[minmax(170px,1fr)_minmax(220px,1fr)_minmax(110px,1fr)_92px_128px_112px_136px_28px]';

const CHANNEL_ICON = { email: Mail, whatsapp: Smartphone, api: Plug, web: Globe, manual: PenLine, insurance: ShieldCheck } as const;

const TONE: Record<string, string> = {
  amber: 'bg-[#FEF3C7] text-[#8A5A00] border-amber-200 hover:bg-[#FDE9A8]',
  orange: 'bg-[#FFEDD5] text-[#9A3412] border-orange-200 hover:bg-[#FFE0B8]',
  red: 'bg-[#FEE2E2] text-[#B42318] border-red-200 hover:bg-[#FCD0D0]',
  lilac: 'bg-[#E5E2FF] text-[#5B5872] border-[#C9C2FF] hover:bg-[#DAD6FF]',
  neutral: 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50',
};

export interface OrderRowProps {
  order: OrderResponse;
  currentUid?: string | null;
  canDelete: boolean;
  expanded: boolean;
  taking: boolean;
  /** Bulk selection (orders and sessions). */
  selected: boolean;
  /** Once anything is selected every row shows its checkbox. */
  selecting: boolean;
  onSelect: (shiftKey: boolean) => void;
  onOpen: () => void;
  onToggleStrip: () => void;
  onTake: () => void;
  onDelete: () => void;
  onUpdated: (order: OrderResponse) => void;
}

/** Row menu (⋯) — every row action that is not the one Next-step button. */
const RowMenu: React.FC<{ items: { key: string; label: string; icon: React.ElementType; onClick: () => void; danger?: boolean }[] }> = ({ items }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('orders.menu.more', { defaultValue: 'More actions' })}
        onClick={() => setOpen(v => !v)}
        className="h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-8 z-20 min-w-[11rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg text-[12px]">
          {items.map((it, i) => (
            <React.Fragment key={it.key}>
              {it.danger && i > 0 && <div className="my-1 border-t border-gray-100" />}
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); it.onClick(); }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 ${it.danger ? 'text-[#B42318]' : 'text-gray-700'}`}
              >
                <it.icon className="w-3.5 h-3.5 flex-shrink-0" />
                {it.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const Avatar: React.FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#EDEDFF] text-[11px] font-bold text-[#4B48C7]" aria-hidden="true">
    {(name.trim()[0] || '?').toUpperCase()}
  </span>
);

const OrderRow: React.FC<OrderRowProps> = ({
  order, currentUid, canDelete, expanded, taking, selected, selecting, onSelect, onOpen, onToggleStrip, onTake, onDelete, onUpdated,
}) => {
  const { t } = useTranslation();
  const isSession = order.kind === 'session';
  // Async API search still building the order (no order row yet).
  const processing = isSession && !!order.processing;
  const stage = stageOf(order);
  const ownerType = ownerTypeOf(order);
  const counts = partsCounts(order);
  const vehicle = vehicleOf(order);
  const step = nextStepOf(order);
  const subject = subjectOf(order);
  const channel = channelOf(order);
  const ChannelIcon = processing ? Plug : isSession ? MessageCircle : CHANNEL_ICON[channel];
  const closed = stage === 'completed' || stage === 'cancelled';
  const dwell = isSession || closed ? '' : formatDwell(hoursSince(order.stage_changed_at || order.updated_at), t);
  const late = !isSession && !closed && isPastSla(order);
  const hasStrip = !isSession && !!order.attention_flag;
  const orderId = processing
    ? t('orders.table.newOrder', { defaultValue: 'New order' })
    : isSession
    ? (order.session_id || '').split('-')[0]
    : (order.external_order_id || `#${order.id}`);
  const updatedLabel = `${t('orders.table.lastUpdated', { defaultValue: 'Last Updated' })}: ${new Date(order.updated_at || order.created_at).toLocaleString()}`;
  const reasons: string[] = !isSession && (order.status === 'pending_assistance' || order.status === 'error') && Array.isArray(order.extra_data?.assistance_reasons)
    ? order.extra_data!.assistance_reasons.filter((r: unknown): r is string => typeof r === 'string')
    : [];
  const stageTitle = [updatedLabel, ...reasons.map(r => t(`orders.assistanceReason.${r}`, { defaultValue: r.replace(/_/g, ' ') }))].join('\n');

  const act = () => (step.target === 'strip' ? onToggleStrip() : onOpen());
  const stepLabel = t(`orders.nextStep.${step.key}`, { defaultValue: step.label });

  const copyVin = () => { if (vehicle.vin) navigator.clipboard?.writeText(vehicle.vin).catch(() => {}); };

  const menu = [
    { key: 'open', label: t('orders.menu.open', { defaultValue: 'Open order' }), icon: ExternalLink, onClick: onOpen },
    ...(!isSession && ownerType === 'unassigned' ? [{ key: 'take', label: t('orders.owner.takeIt', { defaultValue: 'Take it' }), icon: UserCheck, onClick: onTake }] : []),
    ...(hasStrip ? [{ key: 'strip', label: expanded ? t('orders.menu.hideDetails', { defaultValue: 'Hide details' }) : stepLabel, icon: AlertTriangle, onClick: onToggleStrip }] : []),
    ...(vehicle.vin ? [{ key: 'vin', label: t('orders.menu.copyVin', { defaultValue: 'Copy VIN' }), icon: Copy, onClick: copyVin }] : []),
    ...(canDelete ? [{ key: 'delete', label: isSession ? t('orders.actions.deleteSession', { defaultValue: 'Delete session' }) : t('orders.menu.delete', { defaultValue: 'Delete order' }), icon: Trash2, onClick: onDelete, danger: true }] : []),
  ];

  const rowTint = selected
    ? 'bg-[#EDEDFF] hover:bg-[#E4E3FF] ' + (lifecycleRowClass(order).match(/border-l-\S+/g) || []).join(' ')
    : lifecycleRowClass(order) || (order.needs_clarification ? 'bg-[#FEF9EC] hover:bg-[#FDF3D9]' : 'hover:bg-gray-50');

  return (
    <div className={`border-b border-gray-100 last:border-b-0 ${expanded ? 'bg-white' : ''}`}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={hasStrip ? expanded : undefined}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
        className={`group ${ROW_GRID} min-h-[48px] py-1.5 md:py-0 text-[12px] cursor-pointer transition-colors ${rowTint}`}
      >
        {/* Order */}
        <div className="col-start-1 row-start-1 md:col-auto md:row-auto flex items-center gap-2 min-w-0" title={isSession ? order.session_id : (subject || orderId)}>
          {(
            <span className="relative flex h-4 w-4 flex-shrink-0 items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <ChannelIcon className={`w-3.5 h-3.5 text-gray-400 ${selecting ? 'hidden' : 'group-hover:hidden'}`} aria-label={isSession ? t('orders.table.session', { defaultValue: 'Session' }) : t(`orders.channel.${channel}`, { defaultValue: channel })} />
              <input
                type="checkbox"
                checked={selected}
                aria-label={t('orders.bulk.selectRow', { defaultValue: 'Select {{id}}', id: orderId })}
                onClick={(e) => onSelect(e.shiftKey)}
                onChange={() => { /* handled on click so Shift is visible */ }}
                className={`h-4 w-4 cursor-pointer rounded border-gray-300 text-[#3627BF] focus:ring-[#3627BF] ${selecting ? '' : 'hidden group-hover:block'}`}
              />
            </span>
          )}
          <div className="min-w-0 leading-tight">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={`truncate font-inter-tight tabular-nums ${isSession && !processing ? 'text-[12px] text-gray-600' : 'text-[12.5px] font-semibold text-gray-900'}`}>{orderId}</span>
              {order.source === 'insurance_portal' && (
                <span className="flex-shrink-0 inline-flex items-center gap-0.5 rounded bg-teal-50 border border-teal-200 px-1 py-px text-[9.5px] font-bold text-teal-700"
                  title={t('orders.sources.insurance_portal', { defaultValue: 'Insurance Portal' })}>
                  <ShieldCheck className="w-2.5 h-2.5" />{t('orders.insuranceTag', { defaultValue: 'Insurance' })}
                </span>
              )}
            </div>
            <div className="truncate text-[11px] text-[#6B6862]">
              {processing
                ? t('orders.table.viaApi', { defaultValue: 'Via API' })
                : isSession
                ? t('orders.table.session', { defaultValue: 'Session' })
                : subject || (channel === 'manual'
                  ? t('orders.table.manualEntry', { defaultValue: 'Manual entry · {{name}}', name: order.created_by_name || '' }).replace(/ · $/, '')
                  : '')}
            </div>
          </div>
        </div>

        {/* Vehicle */}
        <div className="col-start-1 row-start-2 md:col-auto md:row-auto min-w-0 leading-tight">
          {vehicle.model ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate font-medium text-gray-900">{vehicle.model}</span>
              {vehicle.tag && <span className="flex-shrink-0 rounded bg-gray-100 px-1 py-px text-[9.5px] font-bold text-gray-600">{vehicle.tag}</span>}
            </div>
          ) : order.vin_verification_failed ? (
            <span className="font-medium text-[#B42318]">{t('orders.vehicle.invalidVin', { defaultValue: 'Invalid VIN' })}</span>
          ) : !vehicle.vin ? (
            <span className="text-[#6B6862]">{t('orders.vehicle.unknown', { defaultValue: 'Vehicle unknown' })}</span>
          ) : (stage === 'received' || stage === 'analysing') ? (
            <span className="text-[#6B6862]">{t('orders.vehicle.decoding', { defaultValue: 'Decoding VIN…' })}</span>
          ) : null}
          {(vehicle.plate || vehicle.vin) && (
          <div className="mt-0.5 flex items-center gap-2 min-w-0">
          {vehicle.plate && (
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-900">
              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">{t('orders.vehicle.plate', { defaultValue: 'Plate' })}</span>
              <span className="font-inter-tight tabular-nums">{vehicle.plate}</span>
            </span>
          )}
          {vehicle.vin && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); copyVin(); }}
              title={`${vehicle.vin} · ${t('orders.vehicle.clickToCopy', { defaultValue: 'Click to copy' })}`}
              aria-label={t('orders.vehicle.copyVinAria', { defaultValue: 'Copy VIN {{vin}}', vin: vehicle.vin })}
              className="inline-flex items-center gap-1 text-[11px] text-[#6B6862] hover:text-gray-900"
            >
              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">VIN</span>
              <span className="font-inter-tight tabular-nums">…{vehicle.tail}</span>
            </button>
          )}
          </div>
          )}
        </div>

        {/* Customer (xl+) */}
        <div className="hidden xl:block min-w-0 leading-tight">
          {order.b2b_customer || order.buyer ? (
            <>
              <div className="truncate text-gray-900">{order.b2b_customer?.name || order.buyer}</div>
              <div className="truncate text-[11px] text-[#6B6862]">{order.b2b_customer?.alias || ''}</div>
            </>
          ) : (
            <span className="text-gray-400">{t('orders.table.noCustomer', { defaultValue: 'No customer' })}</span>
          )}
        </div>

        {/* Parts (lg+) */}
        <div className="hidden lg:block leading-tight" title={counts ? t('orders.table.partsReqFoundInCart', { defaultValue: '{{requested}} requested · {{found}} found · {{inCart}} in cart', ...counts }) : undefined}>
          {counts ? (
            <>
              <div className="whitespace-nowrap">
                <span className={`font-semibold tabular-nums ${counts.found === 0 && counts.requested > 0 ? 'text-[#8A5A00]' : 'text-gray-900'}`}>{counts.found}/{counts.requested}</span>
                <span className="text-[#6B6862]"> {t('orders.table.found', { defaultValue: 'found' })}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="relative h-1 w-10 overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
                  <span className="absolute inset-y-0 left-0 bg-[#6866ff]" style={{ width: `${counts.requested ? Math.min(100, (counts.found / counts.requested) * 100) : 0}%` }} />
                  <span className="absolute inset-y-0 left-0 bg-[#0E6E58]" style={{ width: `${counts.requested ? Math.min(100, (counts.inCart / counts.requested) * 100) : 0}%` }} />
                </span>
                <span className="text-[11px] text-[#6B6862] whitespace-nowrap">{t('orders.table.inCartShort', { defaultValue: '{{n}} cart', n: counts.inCart })}</span>
              </div>
            </>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </div>

        {/* Stage · time */}
        <div className="col-start-2 row-start-1 md:col-auto md:row-auto leading-tight justify-self-end md:justify-self-start" title={isSession ? undefined : stageTitle}>
          {processing ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#C9C7FF] bg-[#F0F0FF] px-2 py-px text-[11px] font-semibold text-[#3627BF] whitespace-nowrap">
              <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
              {t('orders.table.processing', { defaultValue: 'Processing…' })}
            </span>
          ) : isSession ? (
            order.needs_clarification || order.vin_verification_failed ? (
              <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${order.vin_verification_failed ? 'text-[#B42318]' : 'text-[#8A5A00]'}`}>
                {order.vin_verification_failed ? <AlertTriangle className="w-3 h-3" /> : <HelpCircle className="w-3 h-3" />}
                {order.vin_verification_failed
                  ? t('orders.table.vinFailed', { defaultValue: 'VIN verification failed' })
                  : t('orders.table.needsClarification', { defaultValue: 'Waiting for answers to questions' })}
              </span>
            ) : (
              <span className="text-gray-300" title={t('orders.table.sessionNoStatus', { defaultValue: 'Chat interaction — not an order yet' })}>—</span>
            )
          ) : (
            <>
              <span className={`inline-flex rounded-full border px-2 py-px text-[11px] font-semibold whitespace-nowrap ${STAGE_STYLE[stage]}`}>
                {t(`orders.stage.${stage}`, { defaultValue: STAGE_LABEL[stage] })}
                {stage === 'in_cart' && counts && counts.inCart > 0 && <span className="ml-1 opacity-80">· {counts.inCart}</span>}
              </span>
              <div className="mt-1 flex items-center gap-2">
                <MicroStepper order={order} />
                {dwell && <span className={`text-[11px] tabular-nums ${late ? 'font-semibold text-[#8A5A00]' : 'text-[#6B6862]'}`}>{dwell}</span>}
              </div>
            </>
          )}
        </div>

        {/* Owner (md+: avatar; lg+: name) */}
        <div className="hidden md:flex items-center gap-1.5 min-w-0">
          {isSession ? (
            <span className="text-gray-300">—</span>
          ) : ownerType === 'unassigned' ? (
            <>
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-dashed border-amber-400 text-[11px] font-bold text-[#8A5A00]" aria-hidden="true">?</span>
              <button
                type="button"
                disabled={taking}
                onClick={(e) => { e.stopPropagation(); onTake(); }}
                className="hidden lg:inline-flex h-[26px] items-center rounded-md border border-[#3627BF] bg-white px-2 text-[11px] font-bold text-[#3627BF] hover:bg-[#F0F0FF] disabled:cursor-wait disabled:opacity-60"
              >
                {taking ? t('orders.owner.taking', { defaultValue: 'Taking…' }) : t('orders.owner.takeIt', { defaultValue: 'Take it' })}
              </button>
            </>
          ) : ownerType === 'agent' ? (
            <>
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500" aria-hidden="true"><Bot className="w-3.5 h-3.5" /></span>
              <span className="hidden lg:inline truncate text-gray-700">{t('orders.owner.agent', { defaultValue: 'Agent' })}</span>
            </>
          ) : (
            <>
              <Avatar name={order.owner_name || order.owner_uid || '?'} />
              <span className="hidden lg:inline truncate text-gray-900" title={order.owner_name || order.owner_uid || ''}>
                {(order.owner_name || order.owner_uid || '').split(/\s+/)[0]}
              </span>
            </>
          )}
        </div>

        {/* Next step */}
        <div className="col-start-2 row-start-2 md:col-auto md:row-auto justify-self-end md:justify-self-start">
          {step.tone === 'muted' ? (
            <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap">{stepLabel}</span>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); act(); }}
              aria-expanded={step.target === 'strip' ? expanded : undefined}
              className={`inline-flex h-[26px] items-center gap-1 rounded-md border px-2.5 text-[11px] font-bold whitespace-nowrap transition-colors ${TONE[step.tone]}`}
            >
              {stepLabel}
              {step.target === 'chat' && <ArrowRight className="w-3 h-3" />}
            </button>
          )}
        </div>

        {/* ⋯ */}
        <div className="col-start-3 row-start-1 md:col-auto md:row-auto justify-self-end">
          <RowMenu items={menu} />
        </div>
      </div>

      {hasStrip && expanded && (
        <OrderAttentionStrip order={order} onUpdated={onUpdated} onOpen={onOpen} currentUid={currentUid} />
      )}
    </div>
  );
};

export default OrderRow;
